import type { FocusEvent } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import type { FieldInputProps, FormikProps } from 'formik';

import makeSize from '@toolshed/styleguide/styles/make-size';
import AppIconButton from '@toolshed/styleguide/components/AppIcon/AppIconButton';
import ScreenReaderOnly from '@toolshed/styleguide/components/ScreenReaderOnly/ScreenReaderOnly';

import useGetLocationHookFactory from '@hooks/use-get-location-hook-factory';

import Input from '@toolshed/styleguide/components/Input/Input';
import { IconLocationPinTarget } from '@toolshed/styleguide/components/AppIcon/AppIcons';
import {
  ErrorWrapper as _ErrorWrapper,
  ErrorMessage,
} from 'components/Search/styles';
import {
  colorPrimary125Dark,
  colorAttention100Default,
} from 'lib/design-system';
import type { CategorySearchValues } from './types';
import { track } from 'lib/sdks/analytics';
import { spacing2 } from 'lib/mortar';
import { colors } from '@checkatrade/web-ui/themes';

export const Wrapper = styled.div<StyledDiv>`
  position: relative;
  width: 100%;
  display: flex;
`;

export const locationLoading = keyframes`
  0% {
    color: ${colors.secondary.DEFAULT};
  }
  100% {
    color: ${colors.secondary.soft};
  }
`;

export const LocationIcon = styled(AppIconButton)`
  right: ${spacing2};
  top: calc(50% - ${makeSize(15)});
  z-index: 1;
  cursor: pointer;
  color: ${colors.secondary.DEFAULT};

  &:focus {
    box-shadow: 0 0 0 4px ${colorAttention100Default};
  }
  &:hover svg {
    fill: ${colorPrimary125Dark};
  }

  ${({ loading }) =>
    !loading
      ? undefined
      : css`
          animation: ${locationLoading} 0.5s ease-in infinite alternate;
        `};
`;

const ErrorWrapper = styled(_ErrorWrapper)`
  display: flex;
  position: unset;
  top: 0;
  transform: none;
  border: 1px solid ${colors.caution.strong};
  border-top: none;
`;

// eslint-disable-next-line react-hooks/rules-of-hooks
const useGetLocation = useGetLocationHookFactory();

export const onSuccessFactory =
  (form: FormikProps<CategorySearchValues>) => (value: string) =>
    form.setFieldValue('location', value);

const LocationField = ({
  form,
  field,
  isSubmitting,
  autoFocus,
  getLocationOnMount,
  locationFinderAbsolute,
  onClick,
  onBlur,
}: LocationFieldProps) => {
  const [gettingLocation, setGettingLocation] = useState(false);
  const onSuccess = useMemo(() => onSuccessFactory(form), [form]);

  const onGetLocation = useGetLocation({
    onUpdateLoading: setGettingLocation,
    onSuccess,
  });

  useEffect(() => {
    if (!getLocationOnMount) {
      return;
    }

    onGetLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onIconClick = useCallback(
    (e: MouseEvent) => {
      track('global.geo_location_pin');
      onGetLocation();
      if (typeof onClick !== 'function') {
        return;
      }

      onClick(e);
    },
    [onClick, onGetLocation],
  );

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      form.submitForm();
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    field.onBlur(event);
    if (onBlur) {
      onBlur(event);
    }
  };

  const gettingLocationLabel = 'Finding...';
  const finalLabel = gettingLocation
    ? gettingLocationLabel
    : 'Postcode (of your project)';
  const textTitle = 'Postcode (of your project)';

  const { location: error } = form.errors;
  const showError = Boolean(!isSubmitting && form.touched.location && error);

  return (
    <>
      <Wrapper>
        <Input
          type="text"
          title={textTitle}
          autoComplete="postal-code"
          label={finalLabel}
          onKeyDown={handleKeyDown}
          small
          autoFocus={autoFocus}
          data-guid="SearchFormLocation"
          hasSelectedBorder={false}
          showError={showError}
          placeholder=""
          required={false}
          aria-label={textTitle}
          {...field}
          onBlur={handleBlur}
        />

        <LocationIcon
          className={locationFinderAbsolute ? 'absolute' : ''}
          autoComplete="postal-code"
          onClick={onIconClick}
          loading={gettingLocation ? 1 : 0}
        >
          <ScreenReaderOnly>
            {gettingLocation ? gettingLocationLabel : 'Pinpoint Location'}
          </ScreenReaderOnly>
          <IconLocationPinTarget
            size="large"
            color={colors.secondary.DEFAULT}
          />
        </LocationIcon>
      </Wrapper>
      {showError && (
        <ErrorWrapper>
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorWrapper>
      )}
    </>
  );
};

export interface LocationFieldProps {
  'aria-label'?: string;
  label?: string | React.ReactNode;
  field: FieldInputProps<string>;
  form: FormikProps<CategorySearchValues>;
  isSubmitting?: boolean;
  autoFocus?: boolean;
  getLocationOnMount?: boolean;
  onClick?: (event: MouseEvent) => void;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  locationFinderAbsolute?: boolean;
}

export default LocationField;
